/**
 * <s-wonder-tour>: drives the Seven Wonders "Grand Tour" collection page.
 * - Marks itself [data-tour-ready]; each [data-chapter] gets [data-in-view] the first time it scrolls in (CSS reveals it).
 * - Fills [data-tour-rail] with one link per chapter (numeral + name) and marks the chapter currently in view with aria-current.
 *   The rail gets [data-visible] while any chapter is on screen.
 * - Drifts each chapter's [data-chapter-drift] outlined name sideways as the chapter scrolls past (skipped with reduced motion).
 * - Size pills (input[data-size-pill]) write their variant id into the form's hidden input[name="id"] and enable [data-wonder-add].
 */
class SWonderTour extends HTMLElement {
  #ac;
  #revealObserver;
  #activeObserver;
  #frame = 0;
  #visible = new Map();

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const chapters = [...this.querySelectorAll('[data-chapter]')];

    this.#buildRail(chapters);

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-in-view', '');
          this.#revealObserver.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.12 }
    );

    this.#activeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#visible.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        this.#updateRail();
      },
      { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] }
    );

    for (const chapter of chapters) {
      this.#revealObserver.observe(chapter);
      this.#activeObserver.observe(chapter);
    }
    this.setAttribute('data-tour-ready', '');

    this.addEventListener('change', (event) => this.#onSizeChange(event), opts);

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.addEventListener('scroll', () => this.#schedule(), { ...opts, passive: true });
      window.addEventListener('resize', () => this.#schedule(), { ...opts, passive: true });
      this.#drift();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#revealObserver?.disconnect();
    this.#activeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #buildRail(chapters) {
    const rail = this.querySelector('[data-tour-rail]');
    if (!rail) return;
    const template = rail.querySelector('template');
    const list = rail.querySelector('ol');
    if (!template || !list) return;
    for (const chapter of chapters) {
      const item = template.content.firstElementChild.cloneNode(true);
      const link = item.querySelector('a');
      link.href = `#${chapter.id}`;
      link.setAttribute('aria-label', (rail.dataset.goTo || '%name%').replace('%name%', chapter.dataset.name || ''));
      const numeral = item.querySelector('[data-rail-numeral]');
      const name = item.querySelector('[data-rail-name]');
      if (numeral) numeral.textContent = chapter.dataset.numeral || '';
      if (name) name.textContent = chapter.dataset.name || '';
      list.append(item);
    }
  }

  #updateRail() {
    const rail = this.querySelector('[data-tour-rail]');
    if (!rail) return;
    let best = null;
    let bestRatio = 0;
    for (const [chapter, ratio] of this.#visible) {
      if (ratio > bestRatio) {
        best = chapter;
        bestRatio = ratio;
      }
    }
    rail.toggleAttribute('data-visible', bestRatio > 0);
    for (const link of rail.querySelectorAll('a')) {
      const active = best && link.getAttribute('href') === `#${best.id}`;
      if (active) link.setAttribute('aria-current', 'step');
      else link.removeAttribute('aria-current');
    }
  }

  #onSizeChange(event) {
    const pill = event.target;
    if (!(pill instanceof HTMLInputElement) || !pill.matches('[data-size-pill]')) return;
    const form = pill.form;
    if (!form) return;
    const input = form.elements.namedItem('id');
    if (input instanceof HTMLInputElement) input.value = pill.value;
    const button = form.querySelector('[data-wonder-add]');
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-describedby');
    }
    const hint = form.querySelector('[data-wonder-hint]');
    if (hint) hint.hidden = true;
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#drift());
  }

  #drift() {
    const viewport = window.innerHeight;
    for (const word of this.querySelectorAll('[data-chapter-drift]')) {
      const chapter = word.closest('[data-chapter]');
      if (!chapter) continue;
      const rect = chapter.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) continue;
      const progress = (viewport - rect.top) / (viewport + rect.height);
      const offset = (0.5 - progress) * 18;
      word.style.transform = `translate3d(${offset.toFixed(2)}vw, 0, 0)`;
    }
  }
}

if (!customElements.get('s-wonder-tour')) customElements.define('s-wonder-tour', SWonderTour);
